import {
  add,
  Duration,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  format as formatDateFns,
  formatDistance as formatDistanceDateFns,
  formatDistanceToNow,
  formatISO,
  Interval,
  isFuture,
  parseISO,
  subMilliseconds,
} from "date-fns";

export type DateType = Date | string | number;

export const parseDateIfNeeded = (date: DateType) =>
  typeof date === "string" ? parseISO(date) : date;

/**
 * If you need to display a date in interface,
 * use Components from `src/base-components/Date.tsx`
 */
export const formatDate = (
  date: DateType,
  format: string = "h:mm aaa, d MMM yyyy",
): string => formatDateFns(parseDateIfNeeded(date), format);

// The default date-fns format is different from moment.js
// This is a custom locale for the `formatDistanceToNow`
// function to match the moment.js format
const FORMAT_DISTANCE_LOCALE = {
  lessThanXSeconds: "a few seconds",
  xSeconds: "{{count}} second{{s}}",
  halfAMinute: "30 seconds",
  lessThanXMinutes: "a few seconds",
  xMinutes: "{{count}} minute{{s}}",
  aboutXHours: "{{count}} hour{{s}}",
  xHours: "{{count}} hour{{s}}",
  xDays: "{{count}} day{{s}}",
  aboutXWeeks: "{{count}} week{{s}}",
  xWeeks: "{{count}} week{{s}}",
  aboutXMonths: "{{count}} month{{s}}",
  xMonths: "{{count}} month{{s}}",
  aboutXYears: "{{count}} year{{s}}",
  xYears: "{{count}} year{{s}}",
  overXYears: "{{count}} year{{s}}",
  almostXYears: "{{count}} year{{s}}",
} as const;

export const formatDistanceToken = (
  token: keyof typeof FORMAT_DISTANCE_LOCALE,
  count: number,
) => {
  const article = token.includes("Hours") ? "an" : "a";
  return FORMAT_DISTANCE_LOCALE[token]
    .replace("{{count}}", count > 1 ? String(count) : article)
    .replace("{{s}}", count > 1 ? "s" : "");
};

export const formatDistance = (
  date: DateType,
  baseDate: DateType,
  options: {
    withoutSuffix?: boolean;
    allowFuture?: boolean;
  } = {
    withoutSuffix: false,
    allowFuture: false,
  },
): string => {
  const { withoutSuffix = false, allowFuture = false } = options;
  const parsedDate = parseDateIfNeeded(date);
  const displayDate =
    !allowFuture && isFuture(parsedDate)
      ? subMilliseconds(new Date(), 1)
      : parsedDate;
  const parsedBaseDate = parseDateIfNeeded(baseDate);

  return formatDistanceDateFns(displayDate, parsedBaseDate, {
    addSuffix: !withoutSuffix,
    locale: {
      formatDistance: (
        token: keyof typeof FORMAT_DISTANCE_LOCALE,
        count: number,
        options,
      ) => {
        const result = formatDistanceToken(token, count);

        if (options && options.addSuffix) {
          return options.comparison && options.comparison > 0
            ? `in ${result}`
            : `${result} ago`;
        }

        return result;
      },
    },
  });
};

export const dateFromNow = (
  date: DateType,
  withoutSuffix?: boolean,
  allowFuture?: boolean,
): string => {
  return formatDistance(date, new Date(), { withoutSuffix, allowFuture });
};

const RETURN_DATE_KEY = "return date";
// The default date-fns format is different from moment.js
// This is a custom locale for the `formatDistanceToNow`
// function to match the moment.js format
const FORMAT_SHORT_DISTANCE_LOCALE = {
  lessThanXSeconds: "now",
  xSeconds: "now",
  halfAMinute: "1m",
  lessThanXMinutes: "now",
  xMinutes: "{{count}}m",
  aboutXHours: "{{count}}h",
  xHours: "{{count}}h",
  xDays: "{{count}}d",
  aboutXWeeks: RETURN_DATE_KEY,
  xWeeks: RETURN_DATE_KEY,
  aboutXMonths: RETURN_DATE_KEY,
  xMonths: RETURN_DATE_KEY,
  aboutXYears: "{{count}}y",
  xYears: "{{count}}y",
  overXYears: "{{count}}y",
  almostXYears: "{{count}}y",
} as const;

export const shortDateFromNow = (
  date: DateType,
  allowFuture?: boolean,
): string => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  const displayDate =
    !allowFuture && isFuture(parsedDate)
      ? subMilliseconds(new Date(), 1)
      : parsedDate;

  return formatDistanceToNow(displayDate, {
    locale: {
      formatDistance: (
        token: keyof typeof FORMAT_SHORT_DISTANCE_LOCALE,
        count: number,
      ) => {
        if (FORMAT_SHORT_DISTANCE_LOCALE[token] === RETURN_DATE_KEY) {
          return format(displayDate, "d MMM");
        } else {
          return FORMAT_SHORT_DISTANCE_LOCALE[token].replace(
            "{{count}}",
            String(count),
          );
        }
      },
    },
  });
};

/**
 * Formats a date as relative time if within threshold days, otherwise as absolute date
 * @param date - The date to format
 * @param thresholdDays - Number of days threshold (default 30)
 * @param format - Date format string for dates outside threshold
 * @returns Formatted string - "X days ago" for recent, formatted date for older
 */
export const formatDateWithThreshold = (
  date: DateType,
  thresholdDays: number = 30,
  format: string = "MMM d, yyyy",
): string => {
  const parsedDate = parseDateIfNeeded(date);
  const diffInDays = differenceInDays(new Date(), parsedDate);

  if (diffInDays >= thresholdDays) {
    return formatDate(parsedDate, format);
  }
  return dateFromNow(parsedDate);
};

const ISO_8601_DATE_PATTERNS = {
  year: "([+-]?\\d{4}(?!\\d{2}\\b))",
  month_and_date: "((-?)((0[1-9]|1[0-2])(\\3([12]\\d|0[1-9]|3[01]))",
  weekdate:
    "W([0-4]\\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\\d|[12]\\d{2}|3([0-5]\\d|6[1-6]))",
  utc_date_time:
    "\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?(Z|[+-]\\d{2}:\\d{2})",
};

const ISO_8601_TIME_PATTERNS = {
  time: "(?:([01]\\d|2[0-3]):?[0-5]\\d:?[0-5]\\d(?:\\.\\d+)?)",
  time_zone: "(?:Z|[+-][0-2]\\d:[0-5]\\d)?",
};

const ISO_8601_DATE_REGEX = new RegExp(
  `^${ISO_8601_DATE_PATTERNS.year}${ISO_8601_DATE_PATTERNS.month_and_date}?|${ISO_8601_DATE_PATTERNS.weekdate}))$`,
);
const ISO_8601_TIME_REGEX = new RegExp(
  `^${ISO_8601_TIME_PATTERNS.time}${ISO_8601_TIME_PATTERNS.time_zone}?$`,
);
const ISO_8601_UTC_DATETIME_REGEX = new RegExp(
  `^${ISO_8601_DATE_PATTERNS.utc_date_time}`,
);

export const isISO8601String = (date: string): boolean => {
  const [datePart, timePart] = date.split(/[T\s]/);

  return (
    ISO_8601_DATE_REGEX.test(datePart) &&
    (!timePart || ISO_8601_TIME_REGEX.test(timePart))
  );
};

// Exact datetime format: YYYY-MM-DDTHH:MM:SS(Z|+HH:MM/-HH:MM)
// Matches: "2020-01-01T00:00:00Z" or "2020-01-01T12:53:00+00:00"
export const isISO8601DateTimeString = (date: string): boolean => {
  return isISO8601String(date) && ISO_8601_UTC_DATETIME_REGEX.test(date);
};

// Checks not only the string format but also
// if the date is parseable by native Date constructor
export const isISO8601 = (date: string): boolean => {
  return isISO8601String(date) && Boolean(new Date(date).getTime());
};

export const getDateOnly = (date: string) => {
  return formatISO(parseISO(date), { representation: "date" });
};

/**
 * Formats duration between two dates with precision (includes hours and minutes)
 * @param date - The end date
 * @param baseDate - The start date
 * @returns Formatted string like "1 hour 23 minutes" or "2 hours 45 minutes"
 */
export const formatPreciseDuration = (
  date: DateType,
  baseDate: DateType,
): string => {
  const parsedDate = new Date(typeof date === "string" ? parseISO(date) : date);
  const parsedBaseDate = new Date(
    typeof baseDate === "string" ? parseISO(baseDate) : baseDate,
  );

  const totalSeconds = Math.abs(
    Math.floor((parsedDate.getTime() - parsedBaseDate.getTime()) / 1000),
  );

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  }

  // Only show seconds if less than a minute total
  if (hours === 0 && minutes === 0) {
    parts.push(`${seconds} second${seconds !== 1 ? "s" : ""}`);
  }

  return parts.length > 0 ? parts.join(" ") : "0 seconds";
};

type DurationMaxDays = {
  years?: 0;
  months?: 0;
  weeks?: 0;
} & Duration;

// Returns a duration object with only days, hours, minutes, and seconds.
// Based on https://github.com/date-fns/date-fns/blob/main/src/intervalToDuration/index.ts
export const intervalToDurationMaxDays = (
  interval: Interval,
): DurationMaxDays => {
  const { start, end } = interval;
  const duration: DurationMaxDays = {};

  const days = differenceInDays(end, start);
  if (days) duration.days = days;

  const remainingHours = add(start, { days: duration.days });
  const hours = differenceInHours(end, remainingHours);
  if (hours) duration.hours = hours;

  const remainingMinutes = add(remainingHours, { hours: duration.hours });
  const minutes = differenceInMinutes(end, remainingMinutes);
  if (minutes) duration.minutes = minutes;

  const remainingSeconds = add(remainingMinutes, { minutes: duration.minutes });
  const seconds = differenceInSeconds(end, remainingSeconds);
  if (seconds) duration.seconds = seconds;

  return duration;
};
